import type { AgentId } from './schema';

export type AgentTitle = 'CEO' | 'COO' | 'CFO' | 'CTO' | 'CRO' | 'CCO' | 'CComO' | 'Head of Sales Ops';

export interface AgentConfig {
  readonly id: AgentId;
  readonly label: string;
  readonly title: AgentTitle;
  readonly reportsTo: AgentId | null;
  readonly businessUnit: 'portfolio' | 'findmyfrag' | 'jar-build-brands' | 'sales-ops';
  readonly room: string;
  readonly statusUrl: string;
  readonly sprite: string;
  readonly color: string;
  readonly dossier: string;
}

const DEV = import.meta.env?.DEV ?? true;

// Production — always points at the live events Worker.
// Role comes from the agent config below; Worker returns aggregated state for
// whichever Claude Code session(s) currently map to that role (by cwd).
const EVENTS_BASE =
  (import.meta.env?.VITE_EVENTS_URL as string | undefined) ??
  'https://command-deck-events.jariust-curry.workers.dev';

const workerUrl = (role: string): string =>
  DEV
    ? `/api/mock/${role}/status`
    : `${EVENTS_BASE}/status?role=${role}`;

export const AGENTS: readonly AgentConfig[] = [
  {
    id: 'jarius',
    label: 'Jarius',
    title: 'CEO',
    reportsTo: null,
    businessUnit: 'portfolio',
    room: 'command-deck',
    statusUrl: workerUrl('jarius'),
    sprite: '/sprites/agents/jarius.svg',
    color: 'var(--color-deck-amber)',
    dossier: 'Founder. Operates from the commander chair. Directs Claude Code sessions.',
  },
  {
    id: 'atlas',
    label: 'Atlas',
    title: 'CFO',
    reportsTo: 'jarius',
    businessUnit: 'portfolio',
    room: 'war-room',
    statusUrl: workerUrl('atlas'),
    sprite: '/sprites/agents/atlas.svg',
    color: 'var(--color-deck-violet)',
    dossier: 'Lights up when a Claude Code session runs in the command-deck project.',
  },
  {
    id: 'muse',
    label: 'Muse',
    title: 'CTO',
    reportsTo: 'atlas',
    businessUnit: 'findmyfrag',
    room: 'factory-findmyfrag',
    statusUrl: workerUrl('muse'),
    sprite: '/sprites/agents/muse.svg',
    color: 'var(--color-deck-cyan)',
    dossier: 'Lights up when a Claude Code session runs in a findmyfrag / fragrance project.',
  },
  {
    id: 'jack',
    label: 'Jack',
    title: 'Head of Sales Ops',
    reportsTo: 'atlas',
    businessUnit: 'sales-ops',
    room: 'sales-ops',
    statusUrl: workerUrl('jack'),
    sprite: '/sprites/agents/jack.svg',
    color: 'var(--color-deck-mint)',
    dossier: 'Lights up when a Claude Code session runs in jack-appointment-setter or lead-capture work.',
  },
] as const;

export const AGENTS_BY_ID: Record<AgentId, AgentConfig> = Object.fromEntries(
  AGENTS.map((a) => [a.id, a]),
) as Record<AgentId, AgentConfig>;

export const POLL_INTERVAL_MS = {
  visible: 3_000,
  hidden: 30_000,
} as const;

export const FETCH_TIMEOUT_MS = 1_500;
